import React, { useState, useEffect } from 'react';
import { useCreateDivisionRole, useUpdateDivisionRole } from '../../hooks/use-division-roles';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import { DivisionRole } from '../../types';
import { Button } from '../ui/button';

interface DivisionRoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  role?: DivisionRole;
}

export function DivisionRoleFormModal({ 
  isOpen, 
  onClose, 
  divisionId, 
  role 
}: DivisionRoleFormModalProps) {
  const [name, setName] = useState('');
  const [includeInRegistration, setIncludeInRegistration] = useState(true);
  const [isDivisionAdmin, setIsDivisionAdmin] = useState(false);
  const [isWorkflowAdmin, setIsWorkflowAdmin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createDivisionRole = useCreateDivisionRole();
  const updateDivisionRole = useUpdateDivisionRole();

  const isEditing = !!role;

  useEffect(() => {
    if (isOpen) {
      setName(role?.name || '');
      setIncludeInRegistration(role ? role.includeInRegistration : true);
      setIsDivisionAdmin(role?.isDivisionAdmin ?? false);
      setIsWorkflowAdmin(role?.isWorkflowAdmin ?? false);
    }
  }, [isOpen, role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      if (isEditing && role) {
        await updateDivisionRole.mutateAsync({
          divisionId,
          roleId: role.id,
          request: { name: name.trim(), includeInRegistration, isDivisionAdmin, isWorkflowAdmin }
        });
      } else {
        await createDivisionRole.mutateAsync({
          divisionId,
          request: { name: name.trim(), includeInRegistration, isDivisionAdmin, isWorkflowAdmin }
        });
      }
      
      handleClose();
    } catch (error) {
      console.error('Failed to save role:', error);
      // Error handling is typically done via react-query's error handling
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setIncludeInRegistration(true);
    setIsDivisionAdmin(false);
    setIsWorkflowAdmin(false);
    setIsSubmitting(false);
    onClose();
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>

        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="division-role-modal-title"
          tabIndex={-1}
          className="relative inline-block transform overflow-hidden rounded-lg bg-white dark:bg-secondary-800 px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle focus:outline-none"
        >
          <div>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
              <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 id="division-role-modal-title" className="text-lg font-medium leading-6 text-secondary-900 dark:text-white">
                {isEditing ? 'Edit Division Role' : 'Create Division Role'}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  {isEditing 
                    ? 'Update the role name for this division.'
                    : 'Create a new role that can be assigned to users in this division.'
                  }
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 sm:mt-6">
            <div>
              <label htmlFor="role-name" className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">
                Role Name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="role-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full rounded-md border-secondary-300 dark:border-secondary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  placeholder="Enter role name (e.g., Senior Approver, Budget Manager)"
                  required
                  autoFocus
                />
              </div>
              <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
                Role names should be descriptive and unique within the division.
              </p>
            </div>

            <div className="mt-4 flex items-start">
              <div className="flex h-5 items-center">
                <input
                  id="role-include-in-registration"
                  type="checkbox"
                  checked={includeInRegistration}
                  onChange={(e) => setIncludeInRegistration(e.target.checked)}
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
              </div>
              <div className="ml-3 text-sm text-left">
                <label htmlFor="role-include-in-registration" className="font-medium text-secondary-700 dark:text-secondary-200">
                  Available during self-registration
                </label>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">
                  When checked, users can select this role on the public registration page.
                </p>
              </div>
            </div>

            <div className="mt-4 border-t border-secondary-200 dark:border-secondary-700 pt-4 text-left">
              <p className="text-xs font-medium uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                Elevated access
              </p>
              <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                Any users assigned this role gain the matching capability below, regardless of the role's name.
              </p>

              <div className="mt-3 flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id="role-is-division-admin"
                    type="checkbox"
                    checked={isDivisionAdmin}
                    onChange={(e) => setIsDivisionAdmin(e.target.checked)}
                    className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="ml-3 text-sm text-left">
                  <label htmlFor="role-is-division-admin" className="font-medium text-secondary-700 dark:text-secondary-200">
                    Division Admin access
                  </label>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Full admin access within this division (users, workflows, division roles, configuration).
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id="role-is-workflow-admin"
                    type="checkbox"
                    checked={isWorkflowAdmin}
                    onChange={(e) => setIsWorkflowAdmin(e.target.checked)}
                    className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="ml-3 text-sm text-left">
                  <label htmlFor="role-is-workflow-admin" className="font-medium text-secondary-700 dark:text-secondary-200">
                    Workflow Admin access
                  </label>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Narrower than Division Admin: can edit workflows, workflow templates, and users only.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <Button
                type="submit"
                disabled={!name.trim() || isSubmitting}
                className="w-full justify-center sm:col-start-2"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  isEditing ? 'Update Role' : 'Create Role'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isSubmitting}
                className="mt-3 w-full justify-center sm:col-start-1 sm:mt-0"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
